import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import type { ThemeFamily, ThemeWithVersions } from "@/lib/api/types";
import { pickLocalized } from "@/lib/i18n/localized";
import { useThemesQuery } from "../services/useThemesQuery";
import { getLatestPublishedVersionNumber } from "../services/theme-versions";
import { ThemeStatusBadge } from "../components/ThemeStatusBadge";
import {
  Button,
  DataTable,
  EmptyState,
  LoadingSkeleton,
  ErrorState,
} from "@a2z/ui";
import type { DataTableColumn } from "@a2z/ui";

const FAMILY_LABEL_KEY: Record<ThemeFamily, string> = {
  starter: keys.themes.families.starter,
};

type ThemesPageProps = {
  locale: Locale;
};

export function ThemesPage({ locale }: ThemesPageProps) {
  const t = useTranslations();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useThemesQuery();

  if (isLoading) {
    return <LoadingSkeleton variant="list" rows={5} />;
  }

  if (error || !data) {
    return <ErrorState onRetry={refetch} />;
  }

  const columns: DataTableColumn<ThemeWithVersions>[] = [
    {
      id: "name",
      header: t(keys.themes.listName),
      cell: (theme) => (
        <div className="min-w-0">
          <div className="truncate font-semibold text-[color:var(--text-primary)]">
            {pickLocalized(locale, theme.nameAr, theme.nameEn)}
          </div>
          <div className="truncate text-[11px] text-[color:var(--text-muted)]">
            {theme.slug}
          </div>
        </div>
      ),
    },
    {
      id: "family",
      header: t(keys.themes.family),
      width: "120px",
      // Fall back to the raw family name when the catalog carries a family the
      // admin has no label key for (the seeded eo-* themes). Passing an
      // undefined key into t() is what crashed the whole page (t → .split on
      // undefined). Mirrors the seller gallery's `nicheLabels[n] ?? n`.
      cell: (theme) => {
        const key = FAMILY_LABEL_KEY[theme.family];
        return key ? t(key) : theme.family;
      },
    },
    {
      id: "status",
      header: t(keys.common.status),
      width: "120px",
      cell: (theme) => <ThemeStatusBadge status={theme.status} />,
    },
    {
      id: "versions",
      header: t(keys.themes.versions),
      width: "90px",
      align: "center",
      cell: (theme) => theme.versions.length,
    },
    {
      id: "latestPublished",
      header: t(keys.themes.latestPublished),
      width: "140px",
      align: "center",
      cell: (theme) => {
        const latest = getLatestPublishedVersionNumber(theme.versions);
        return latest === null ? "—" : `v${latest}`;
      },
    },
    {
      id: "sortWeight",
      header: t(keys.themes.sortWeight),
      width: "110px",
      align: "center",
      cell: (theme) => theme.sortWeight,
    },
  ];

  return (
    <section className="flex w-full max-w-4xl flex-col gap-5 lg:px-6 lg:py-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[23px] font-bold leading-tight tracking-[-0.025em] lg:text-[28px] lg:font-semibold lg:tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.themes.title)}
          </h1>
        </div>
        <Button
          type="button"
          variant="primary"
          onClick={() => navigate(`/${locale}/admin/themes/new`)}
        >
          {t(keys.themes.create)}
        </Button>
      </div>
      <DataTable
        rows={data}
        columns={columns}
        rowKey={(theme) => theme.id}
        ariaLabel={t(keys.themes.title)}
        onRowClick={(theme) =>
          navigate(`/${locale}/admin/themes/${theme.id}`)
        }
        empty={
          <EmptyState
            title={t(keys.common.emptyTitle)}
            description={t(keys.themes.emptySubtitle)}
          />
        }
      />
    </section>
  );
}
